<template>
    <div
        class="upload-queue"
        @dragover.prevent="dragOver = true"
        @dragenter.prevent="dragOver = true"
        @dragleave.prevent="dragOver = false"
        @drop.prevent="onDrop"
    >
        <div class="drop-zone" :class="{'is-active': dragOver}">
            <input
                ref="fileInput"
                type="file"
                multiple
                class="hidden-input"
                @change="onInputChange"
            >
            <div class="drop-zone-content">
                <i class="fad fa-cloud-arrow-up fa-2x mb-2"></i>
                <div><strong>Drag and drop files here</strong> or
                    <a @click="$refs.fileInput.click()">click to browse</a>.
                </div>
                <div class="is-size-7 has-text-grey mt-1">
                    Multiple files supported. Videos upload one at a time;
                    images and documents upload in parallel.
                </div>
            </div>
        </div>

        <div v-if="items.length" class="queue-list mt-3">
            <div class="queue-header">
                <span class="is-size-7">
                    {{ completedCount }} of {{ items.length }} complete{{ failedCount ? `, ${failedCount} failed` : '' }}
                </span>
                <a class="is-size-7" @click="clearDone" v-if="completedCount || failedCount">Clear finished</a>
            </div>
            <div class="queue-row" v-for="item in items" :key="item.id">
                <div class="queue-row-main">
                    <i class="fad fa-fw mr-2" :class="rowIcon(item)"></i>
                    <span class="queue-title">{{ item.file.name }}</span>
                    <span class="queue-status has-text-grey is-size-7">{{ statusLabel(item) }}</span>
                </div>
                <b-progress
                    v-if="item.status === 'uploading' || item.status === 'processing'"
                    type="is-info"
                    size="is-small"
                    :value="item.status === 'processing' ? null : item.percent"
                />
                <div v-if="item.status === 'failed'" class="has-text-danger is-size-7">
                    {{ item.error || 'Upload failed' }}
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import { axiosInstance, apiUrl } from "@/http_client";

    // Image/PDF/etc can upload in parallel. Video processing is heavy
    // server-side (ffmpeg probe + thumbnail), so we serialize it.
    const LIGHT_CONCURRENCY = 3;
    const HEAVY_CONCURRENCY = 1;

    function isHeavyFile(file) {
        return (file.type || '').startsWith('video');
    }

    let nextId = 1;

    export default {
        props: {
            provider: { type: [Number, String], default: null },
            folder: { type: [Number, String], default: null }
        },
        data() {
            return {
                dragOver: false,
                items: [],
                lightActive: 0,
                heavyActive: 0
            };
        },
        computed: {
            completedCount() {
                return this.items.filter(i => i.status === 'done').length;
            },
            failedCount() {
                return this.items.filter(i => i.status === 'failed').length;
            }
        },
        methods: {
            onDrop(e) {
                this.dragOver = false;
                let files = Array.from(e.dataTransfer.files || []);
                if (files.length) this.enqueue(files);
            },
            onInputChange(e) {
                let files = Array.from(e.target.files || []);
                if (files.length) this.enqueue(files);
                e.target.value = '';
            },
            enqueue(files) {
                files.forEach(f => {
                    this.items.push({
                        id: nextId++,
                        file: f,
                        status: 'queued',
                        percent: 0,
                        error: null,
                        heavy: isHeavyFile(f)
                    });
                });
                this.pump();
            },
            pump() {
                for (let item of this.items) {
                    if (item.status !== 'queued') continue;
                    if (item.heavy && this.heavyActive >= HEAVY_CONCURRENCY) continue;
                    if (!item.heavy && this.lightActive >= LIGHT_CONCURRENCY) continue;
                    this.startUpload(item);
                }
            },
            startUpload(item) {
                item.status = 'uploading';
                if (item.heavy) this.heavyActive++;
                else this.lightActive++;

                let formData = new FormData();
                formData.append('file', item.file);
                if (this.folder !== null && this.folder !== undefined && this.folder !== 'all' && this.folder !== 'null') {
                    formData.append('folder_id', this.folder);
                }
                if (this.provider) formData.append('content_provider_id', this.provider);

                axiosInstance.post(apiUrl + 'library/upload', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        if (!progressEvent.total) return;
                        item.percent = Math.round((progressEvent.loaded / progressEvent.total) * 100);
                        if (item.percent >= 100) item.status = 'processing';
                    }
                }).then(response => {
                    item.status = 'done';
                    item.percent = 100;
                    this.$emit('uploaded', response.data);
                }).catch(err => {
                    console.error('Upload failed', err);
                    item.status = 'failed';
                    item.error = (err && err.response && err.response.data && err.response.data.message) || 'Upload failed';
                    this.$emit('failed', { item, error: err });
                }).finally(() => {
                    if (item.heavy) this.heavyActive--;
                    else this.lightActive--;
                    this.pump();
                });
            },
            clearDone() {
                this.items = this.items.filter(i => i.status !== 'done' && i.status !== 'failed');
            },
            statusLabel(item) {
                switch (item.status) {
                    case 'queued': return 'Waiting…';
                    case 'uploading': return item.percent + '%';
                    case 'processing': return 'Processing…';
                    case 'done': return 'Done';
                    case 'failed': return 'Failed';
                }
                return '';
            },
            rowIcon(item) {
                if (item.status === 'done') return 'fa-circle-check has-text-success';
                if (item.status === 'failed') return 'fa-circle-exclamation has-text-danger';
                if (item.status === 'processing') return 'fa-gear fa-spin';
                if (item.status === 'uploading') return 'fa-cloud-arrow-up';
                return 'fa-clock';
            }
        }
    };
</script>

<style lang="scss" scoped>
    .upload-queue {
        padding: 0.5rem 1rem 1rem;
    }

    .drop-zone {
        border: 2px dashed #c9d2de;
        border-radius: 6px;
        padding: 1.25rem;
        text-align: center;
        color: #4a4a4a;
        background: #fafbfc;
        transition: all 150ms;
        cursor: pointer;

        &.is-active {
            border-color: #336699;
            background: #eef4fb;
        }

        i {
            color: #3c6691;
        }
    }

    .hidden-input {
        display: none;
    }

    .queue-header {
        display: flex;
        justify-content: space-between;
        align-items: center;
        margin-bottom: 0.5rem;

        a {
            cursor: pointer;
        }
    }

    .queue-row {
        padding: 0.5rem 0.75rem;
        border: 1px solid #eaeaea;
        border-radius: 4px;
        margin-bottom: 0.35rem;
        background: #ffffff;
    }

    .queue-row-main {
        display: flex;
        align-items: center;
        gap: 0.35rem;
        margin-bottom: 0.25rem;
    }

    .queue-title {
        flex: 1;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    .queue-status {
        white-space: nowrap;
    }
</style>
